import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Lock, ShieldAlert, Fingerprint, ServerCog, HeartHandshake, Languages, Users, GraduationCap, Globe2, Sparkles } from "lucide-react";
import { SOLUTION_TABS, SECURITY_PILLARS, BHARAT_TRAITS, BHARAT_CARDS, INNOVATIONS, IMPACT_TARGETS, VALIDATIONS, ROADMAP } from "@/lib/data";
import { SectionHead, Reveal, Card, Tag, TextLink, CountUp, Eyebrow, btnPrimary, btnGhost, ArrowIcon, EASE } from "@/components/primitives";
import { ArchStack, Flywheel, BharatGrid } from "@/components/visuals/Diagrams";
import { EcosystemNet } from "@/components/visuals/Network";

const SEC_ICONS = { Lock, ShieldAlert, Fingerprint, ServerCog };
const BHARAT_ICONS = { HeartHandshake, Languages, Users, GraduationCap, Globe2 };

export function SolutionsTabs() {
  const [active, setActive] = useState(0);
  const tab = SOLUTION_TABS[active];
  return (
    <section className="border-b border-line bg-abyss/50 py-24 sm:py-32" data-testid="solutions-tabs-section">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-8">
        <SectionHead eyebrow="Chapter 06 — Who It Serves" title="One Platform. Multiple Ecosystem Participants." />
        <Reveal className="mt-12">
          <div className="flex gap-2 overflow-x-auto pb-2" role="tablist" aria-label="Solution audiences">
            {SOLUTION_TABS.map((t, i) => (
              <button
                key={t.id}
                role="tab"
                aria-selected={active === i}
                data-testid={`solution-tab-${t.id}`}
                onClick={() => setActive(i)}
                className={`whitespace-nowrap rounded-full border px-5 py-2.5 text-sm font-medium transition-colors duration-300 ${
                  active === i ? "border-electric bg-electric/10 text-electric" : "border-line text-mute hover:border-electric/40 hover:text-mist"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </Reveal>
        <div className="mt-8 rounded-2xl border border-line bg-panel/70 p-8 sm:p-12">
          <AnimatePresence mode="wait">
            <motion.div
              key={tab.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.4, ease: EASE }}
              data-testid={`solution-panel-${tab.id}`}
            >
              <h3 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-mist">{tab.headline}</h3>
              <ul className="mt-8 grid gap-5 sm:grid-cols-3">
                {tab.points.map((p, i) => (
                  <li key={i} className="flex gap-3 text-sm leading-relaxed text-mute">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal" aria-hidden="true" />
                    {p}
                  </li>
                ))}
              </ul>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}

export function ArchSection() {
  return (
    <section className="theme-dark border-b border-line bg-ink py-24 sm:py-32" data-testid="architecture-section">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-8">
        <SectionHead
          eyebrow="Chapter 07 — Architecture"
          title="Designed as an Intelligence-First Financial Infrastructure"
          lead="Explore each layer of the stack — from the experiences people touch to the ecosystem integrations beneath."
        />
        <Reveal className="mt-14">
          <ArchStack />
        </Reveal>
      </div>
    </section>
  );
}

export function SecurityPillars() {
  return (
    <section className="border-b border-line bg-abyss/50 py-24 sm:py-32" data-testid="security-section">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-8">
        <SectionHead eyebrow="Chapter 08 — Security" title="Security Isn't a Feature. It's the Foundation." />
        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {SECURITY_PILLARS.map((p, i) => {
            const Icon = SEC_ICONS[p.icon];
            return (
              <Reveal key={p.title} delay={i * 0.07}>
                <Card testid={`security-pillar-${i}`} className="group h-full p-7">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-line bg-abyss text-electric transition-colors duration-300 group-hover:border-electric/50">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-5 font-display text-base font-semibold text-mist">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-mute">{p.body}</p>
                </Card>
              </Reveal>
            );
          })}
        </div>
        <Reveal delay={0.15} className="mt-10 max-w-3xl">
          <p className="text-sm leading-relaxed text-mute">
            "TimePe is being architected around security, privacy, regulatory alignment and responsible
            technology from the foundation."
          </p>
          <p className="mt-3 text-xs leading-relaxed text-mute/70">
            We do not claim certifications, licences or regulatory approvals that we do not hold.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

export function BharatSection() {
  return (
    <section className="relative overflow-hidden border-b border-line py-24 sm:py-32" data-testid="bharat-section">
      <div className="pointer-events-none absolute -left-40 top-0 h-[500px] w-[500px] bg-glow-teal" aria-hidden="true" />
      <div className="relative mx-auto max-w-[1440px] px-6 lg:px-8">
        <SectionHead
          eyebrow="Chapter 09 — Bharat"
          title="Built in Bharat. Designed for Bharat's Real World."
          lead="Bharat doesn't need technology simply translated for it. It needs technology designed around its realities."
        />
        <div className="mt-14 grid items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <BharatGrid />
            <div className="mt-6 flex flex-wrap gap-2">
              {BHARAT_TRAITS.map((t) => (
                <span key={t} className="rounded-full border border-line bg-panel/70 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-mute">
                  {t}
                </span>
              ))}
            </div>
          </Reveal>
          <div className="grid gap-5 sm:grid-cols-2">
            {BHARAT_CARDS.map((c, i) => {
              const Icon = BHARAT_ICONS[c.icon];
              return (
                <Reveal key={c.title} delay={i * 0.06} className={i === 4 ? "sm:col-span-2" : ""}>
                  <Card testid={`bharat-card-${i}`} className="group h-full p-6">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-line bg-abyss text-teal transition-colors duration-300 group-hover:border-teal/50">
                      <Icon className="h-5 w-5" />
                    </span>
                    <h3 className="mt-4 font-display text-base font-semibold text-mist">{c.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-mute">{c.body}</p>
                  </Card>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

export function EcosystemPreview() {
  return (
    <section className="border-b border-line bg-abyss/50 py-24 sm:py-32" data-testid="ecosystem-preview">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <SectionHead
            eyebrow="Chapter 10 — Ecosystem"
            title="Connecting the Financial Ecosystem."
            lead="Banks, NBFCs, FinTechs, PSPs, insurers, agents, retailers, merchants, citizens and institutions — one trusted fabric."
          >
          </SectionHead>
          <Reveal delay={0.1}>
            <EcosystemNet />
          </Reveal>
        </div>
        <Reveal delay={0.2} className="mt-4 lg:-mt-10">
          <TextLink to="/ecosystem" testid="ecosystem-preview-cta">Build With Us</TextLink>
        </Reveal>
      </div>
    </section>
  );
}

export function InnovationGrid({ chapter = "Chapter 11 — Innovation", cta = true }) {
  return (
    <section className="theme-dark border-b border-line bg-ink py-24 sm:py-32" data-testid="innovation-section">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-8">
        <SectionHead eyebrow={chapter} title="Innovation Is Our Operating System." />
        <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {INNOVATIONS.map((item, i) => (
            <Reveal key={item} delay={i * 0.05} className={i === 6 ? "sm:col-span-2 lg:col-span-1" : ""}>
              <Card testid={`innovation-item-${i}`} className="group flex h-full items-center gap-4 p-6">
                <span className="font-mono text-xs text-electric">{String(i + 1).padStart(2, "0")}</span>
                <span className="flex items-center gap-3 font-display text-base font-semibold text-mist">
                  {i === 6 && <Sparkles className="h-4 w-4 text-teal" />}
                  {item}
                </span>
              </Card>
            </Reveal>
          ))}
        </div>
        {cta && (
          <Reveal delay={0.2} className="mt-10">
            <TextLink to="/innovation" testid="innovation-cta">Explore Our Innovation</TextLink>
          </Reveal>
        )}
      </div>
    </section>
  );
}

export function MoatSection() {
  return (
    <section className="border-b border-line bg-abyss/50 py-24 sm:py-32" data-testid="moat-section">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-8">
        <SectionHead
          eyebrow="Chapter 12 — The Long Game"
          title="Building a Moat That Compounds."
          lead="Distribution creates journeys, journeys create intelligence signals, intelligence builds better experiences — and the flywheel turns."
        />
        <Reveal className="mt-14">
          <Flywheel />
        </Reveal>
        <Reveal delay={0.15} className="mt-12 text-center">
          <p className="font-display text-lg sm:text-xl font-semibold tracking-tight text-mist" data-testid="moat-statement">
            Technology + Data Intelligence + Distribution + Compliance + Network + IP
          </p>
          <p className="mx-auto mt-4 max-w-xl text-xs leading-relaxed text-mute/80">
            This is our long-term moat strategy — a compounding advantage built over time, not an advantage we claim today.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

export function ImpactTargets() {
  return (
    <section className="border-b border-line py-24 sm:py-32" data-testid="impact-section">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-8">
        <SectionHead eyebrow="Chapter 13 — Impact" title="Expanding Access. Building Trust." />
        <Reveal className="mt-10">
          <div className="inline-flex items-center gap-2 rounded-full border border-amber/30 bg-amber/10 px-4 py-2" data-testid="impact-targets-label">
            <span className="h-1.5 w-1.5 rounded-full bg-amber" aria-hidden="true" />
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber">
              Targets & Vision — forward-looking goals, not achieved traction
            </span>
          </div>
        </Reveal>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {IMPACT_TARGETS.map((t, i) => (
            <Reveal key={t.label} delay={i * 0.07}>
              <Card testid={`impact-target-${i}`} className="h-full p-7">
                <p className="font-display text-4xl font-bold tracking-tight text-mist">
                  {t.value !== undefined ? <CountUp to={t.value} suffix={t.suffix} /> : t.text}
                </p>
                <p className="mt-3 text-sm text-mute">{t.label}</p>
                <Tag tone="target" className="mt-4">Target</Tag>
              </Card>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Validation() {
  return (
    <section className="border-b border-line bg-abyss/50 py-24 sm:py-32" data-testid="validation-section">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-8">
        <SectionHead
          eyebrow="Chapter 14 — Foundations"
          title="From Idea to Institutional Validation."
          lead="Building with strong institutional foundations."
        />
        <div className="mt-14 grid gap-5 lg:grid-cols-2">
          {VALIDATIONS.map((v, i) => (
            <Reveal key={v.org} delay={i * 0.06}>
              <Card testid={`validation-card-${i}`} className="flex h-full flex-col gap-6 p-6 sm:flex-row sm:items-center sm:gap-7 sm:p-7">
                <div className="flex shrink-0 flex-wrap items-center justify-center gap-x-5 gap-y-4 sm:w-[30%] sm:flex-col sm:gap-5">
                  {v.logos.map((src, li) => (
                    <img
                      key={src}
                      src={src}
                      alt={li === 0 ? v.logoAlt : ""}
                      aria-hidden={li > 0 || undefined}
                      loading="lazy"
                      className="max-h-16 w-auto max-w-[44%] object-contain sm:max-h-24 sm:w-auto sm:max-w-full"
                    />
                  ))}
                </div>
                <div className="min-w-0 flex-1 text-center sm:text-left">
                  <h3 className="font-display text-xl font-bold tracking-tight text-mist">{v.org}</h3>
                  <p className="mt-2 text-sm text-mute">{v.program}</p>
                  <p className="mt-1 text-xs text-mute/80">{v.institution} • {v.location}</p>
                  <div className="mt-5 flex justify-center sm:justify-start">
                    <Tag tone="dev">{v.status}</Tag>
                  </div>
                </div>
              </Card>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2} className="mt-8">
          <p className="max-w-2xl text-xs leading-relaxed text-mute/80">
            Institutional logos are displayed only where legally permitted. We do not claim partnerships,
            investors, customers or certifications beyond those listed here.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

export function VisionMission() {
  return (
    <section className="relative overflow-hidden border-b border-line py-24 sm:py-32" data-testid="vision-section">
      <div className="pointer-events-none absolute -right-40 bottom-0 h-[500px] w-[500px] bg-glow-blue" aria-hidden="true" />
      <div className="relative mx-auto max-w-[1440px] px-6 lg:px-8">
        <SectionHead eyebrow="Chapter 16 — Vision" title="Our Vision" />
        <Reveal className="mt-12">
          <p className="max-w-4xl font-display text-2xl sm:text-3xl lg:text-4xl font-semibold leading-snug tracking-tight text-mist" data-testid="vision-statement">
            To build Bharat's Sovereign Financial Infrastructure — a secure, intelligent, and fraud-resilient,
            AI-driven, compliance-first trust layer that powers digital finance across India and emerging
            markets, enabling a future where every financial transaction is{" "}
            <span className="text-teal">trusted by design</span>,{" "}
            <span className="text-electric">protected by intelligence</span>, and{" "}
            <span className="text-mist">governed with transparency</span>.
          </p>
        </Reveal>
        <Reveal delay={0.15} className="mt-12 max-w-3xl rounded-2xl border border-line bg-panel/70 p-8">
          <Eyebrow>Mission</Eyebrow>
          <p className="mt-4 text-base leading-relaxed text-mute">
            To strengthen India's digital financial sovereignty by building India-built, sovereign,
            AI-powered and compliance-first infrastructure that makes digital finance more secure,
            private, resilient and trustworthy.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

export function Roadmap({ chapter = "Chapter 17 — Roadmap" }) {
  return (
    <section className="border-b border-line bg-abyss/50 py-24 sm:py-32" data-testid="roadmap-section">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-8">
        <SectionHead eyebrow={chapter} title="From Infrastructure to Ecosystem" />
        <div className="relative mt-16">
          <div className="absolute bottom-0 left-[7px] top-0 w-px bg-line lg:left-0 lg:top-[7px] lg:h-px lg:w-full lg:bg-gradient-to-r lg:from-electric/40 lg:via-line lg:to-line" aria-hidden="true" />
          <div className="grid gap-12 lg:grid-cols-5 lg:gap-6">
            {ROADMAP.map((p, i) => (
              <Reveal key={p.phase} delay={i * 0.08}>
                <div data-testid={`roadmap-phase-${i + 1}`} className="relative pl-8 lg:pl-0">
                  <span className={`absolute left-0 top-0 h-[15px] w-[15px] rounded-full border-2 ${p.tone === "dev" ? "border-teal bg-teal/20" : p.tone === "future" ? "border-mute/50 bg-ink" : "border-electric/60 bg-electric/10"} lg:relative lg:mb-6 lg:block`} aria-hidden="true" />
                  <div className="flex items-center gap-3 lg:mt-0">
                    <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-mute">{p.phase}</p>
                    <Tag tone={p.tone}>{p.status}</Tag>
                  </div>
                  <h3 className="mt-3 font-display text-xl font-semibold text-mist">{p.title}</h3>
                  <ul className="mt-4 space-y-2">
                    {p.items.map((item) => (
                      <li key={item} className="flex items-center gap-2 text-sm text-mute">
                        <span className="h-1 w-1 rounded-full bg-electric/70" aria-hidden="true" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <Reveal delay={0.2} className="mt-12">
          <p className="text-xs leading-relaxed text-mute/80">
            Future phases describe planned capabilities and are subject to change as the platform matures.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

export function FinalCTA() {
  return (
    <section className="relative overflow-hidden py-28 sm:py-36" data-testid="final-cta">
      <div className="pointer-events-none absolute inset-0 bg-grid opacity-60" aria-hidden="true" />
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[420px] w-[720px] -translate-x-1/2 -translate-y-1/2 bg-glow-blue" aria-hidden="true" />
      <div className="relative mx-auto max-w-4xl px-6 text-center">
        <Reveal>
          <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-mist">
            Let's Build Trusted Financial Infrastructure Together.
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-base sm:text-lg text-mute">
            For Banks. FinTechs. NBFCs. Institutions. Merchants. Agents. Investors.
          </p>
        </Reveal>
        <Reveal delay={0.12} className="mt-10 flex flex-wrap justify-center gap-4">
          <Link to="/partners" data-testid="final-cta-partner" className={btnPrimary}>
            Partner With TimePe <ArrowIcon />
          </Link>
          <Link to="/contact" data-testid="final-cta-contact" className={btnGhost}>
            Talk to Avyagrah <ArrowIcon />
          </Link>
        </Reveal>
        <Reveal delay={0.2} className="mt-12">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-teal">
            Secure for Bharat. Built in Bharat, for Bharat.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
